import { useEffect, useMemo, useState } from 'react';
import { getBusinessSubTypeOptions } from '@/services/ant-design-pro/api';

export type BusinessSubTypeSelectOption = {
  /** 子类型 ID，一律用 string，避免 int64 转 Number 丢精度 */
  value: string;
  label: string;
};

export type UseBusinessSubTypeOptionsParams = {
  businessLines?: number | null;
  businessType?: number | null;
  /** 历史回显：详情中的子类型 ID（可能已停用，不在 options 中） */
  snapshotId?: number | string | null;
  /** 历史回显：详情中的子类型名称快照 */
  snapshotName?: string | null;
};

/** 将子类型 ID 规范为 string；空值返回 undefined（不经 Number，避免精度丢失） */
export function normalizeBusinessSubTypeId(
  raw: number | string | null | undefined,
): string | undefined {
  if (raw === null || raw === undefined || raw === '') {
    return undefined;
  }
  const s = String(raw).trim();
  return s === '' ? undefined : s;
}

/**
 * 提交前按当前业务领域+类型实时校验子类型：
 * - 无启用选项 / 未选 → 返回空字符串
 * - 有启用选项 → 保留表单中的 Id（string，含停用快照）
 */
export async function resolveBusinessSubTypeIdForSubmit(params: {
  businessLines?: number | null;
  businessType?: number | null;
  currentId?: number | string | null;
}): Promise<string> {
  const businessType = Number(params.businessType);
  if (!Number.isFinite(businessType) || businessType <= 0) {
    return '';
  }
  const businessLines = Number(params.businessLines);
  if (!Number.isFinite(businessLines)) {
    return '';
  }

  try {
    const res = await getBusinessSubTypeOptions({
      businessLines,
      businessType,
    });
    const list = Array.isArray(res?.result) ? res.result : [];
    if (list.length === 0) {
      return '';
    }
    return normalizeBusinessSubTypeId(params.currentId) ?? '';
  } catch {
    return normalizeBusinessSubTypeId(params.currentId) ?? '';
  }
}

/**
 * 按业务领域 + 业务类型拉取启用中的业务子类型选项。
 * 若快照 ID 不在启用列表中，注入一条选项以保证 Select 可回显。
 *
 * 显隐请用 remoteOptions.length（勿用含快照注入的 options），并须等 settled，
 * 避免详情回填后、请求返回前误判为空而清掉回显。
 */
export function useBusinessSubTypeOptions({
  businessLines,
  businessType,
  snapshotId,
  snapshotName,
}: UseBusinessSubTypeOptionsParams) {
  const [remoteOptions, setRemoteOptions] = useState<BusinessSubTypeSelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadedKey, setLoadedKey] = useState('');

  const canFetch =
    businessLines !== null &&
    businessLines !== undefined &&
    businessType !== null &&
    businessType !== undefined &&
    Number(businessType) > 0;

  const requestKey = canFetch ? `${Number(businessLines)}:${Number(businessType)}` : '';
  /** 当前领域+类型的选项是否已拉取完成（避免回填后、请求返回前误判为空） */
  // 注意：!canFetch 时 settled 也为 true，清空逻辑必须再判断 canFetch，否则会误清详情回显
  const settled = !canFetch || (!loading && loadedKey === requestKey);

  const normalizedSnapshotId = useMemo(
    () => normalizeBusinessSubTypeId(snapshotId),
    [snapshotId],
  );

  useEffect(() => {
    if (!canFetch) {
      setRemoteOptions([]);
      setLoading(false);
      setLoadedKey('');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setRemoteOptions([]);

    getBusinessSubTypeOptions({
      businessLines: Number(businessLines),
      businessType: Number(businessType),
    })
      .then((res) => {
        if (cancelled) return;
        const list = res?.result ?? [];
        setRemoteOptions(
          (Array.isArray(list) ? list : [])
            .filter((item) => item?.id !== null && item?.id !== undefined && item?.id !== '')
            .map((item) => ({
              value: String(item.id),
              label: item.name,
            })),
        );
      })
      .catch(() => {
        if (cancelled) return;
        setRemoteOptions([]);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setLoadedKey(requestKey);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [businessLines, businessType, canFetch, requestKey]);

  const options = useMemo(() => {
    if (
      normalizedSnapshotId === undefined ||
      remoteOptions.some((opt) => opt.value === normalizedSnapshotId)
    ) {
      return remoteOptions;
    }
    // 仅在有启用项时注入停用快照，用于回显；无启用项时不注入，配合显隐隐藏整项
    if (remoteOptions.length === 0) {
      return remoteOptions;
    }
    return [
      ...remoteOptions,
      {
        value: normalizedSnapshotId,
        label: snapshotName || normalizedSnapshotId,
      },
    ];
  }, [remoteOptions, normalizedSnapshotId, snapshotName]);

  return { options, remoteOptions, loading, canFetch, settled };
}

/** 已注册的显隐标记字段名（registerOnly，不提交） */
export const BUSINESS_SUB_TYPE_VISIBLE_FIELD = '__businessSubTypeVisible';
